/**
 * ContactOptionCard
 *
 * Compact contact card for transmission selection.
 * Shows channel type, formatted contact value, and optional department label.
 * For insurance carriers, includes an edit button for the contact handle.
 */

import { Check, Mail, MapPin, Pencil, Printer } from 'lucide-react';
import React, { useState } from 'react';

import { cn } from '@/lib/utils';

import type { ContactChannel, RecipientContact } from '../../types';
import { EditContactHandleModal } from './EditContactHandleModal';

interface ContactOptionCardProps {
  contact: RecipientContact;
  channel: ContactChannel;
  isSelected: boolean;
  onSelect: () => void;
  isDisabled?: boolean;
}

const CHANNEL_CONFIG: Record<
  ContactChannel,
  { icon: React.ElementType; label: string }
> = {
  fax: { icon: Printer, label: 'Fax' },
  email: { icon: Mail, label: 'Email' },
  mailed_letter: { icon: MapPin, label: 'Mail' },
};

/** Format phone number for display */
function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 11 && cleaned.startsWith('1')) {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

interface FormattedValue {
  primary: string;
  secondary?: string;
}

function getFormattedValue(
  contact: RecipientContact,
  channel: ContactChannel,
): FormattedValue | null {
  switch (channel) {
    case 'fax':
      if (!contact.faxNumber) return null;
      return { primary: formatPhoneNumber(contact.faxNumber) };

    case 'email':
      if (!contact.email) return null;
      return { primary: contact.email };

    case 'mailed_letter': {
      if (!contact.address) return null;
      const cityState = [contact.address.city, contact.address.state]
        .filter(Boolean)
        .join(', ');
      return {
        primary: contact.address.address_line1,
        secondary: cityState || undefined,
      };
    }

    default:
      return null;
  }
}

export const ContactOptionCard: React.FC<ContactOptionCardProps> = ({
  contact,
  channel,
  isSelected,
  onSelect,
  isDisabled = false,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const config = CHANNEL_CONFIG[channel];
  const Icon = config.icon;
  const formatted = getFormattedValue(contact, channel);

  // Only show edit for insurance carriers
  const isInsuranceCarrier = contact.source.entityType === 'insurance_carrier';

  if (!formatted) return null;

  const getButtonStyle = () => {
    if (isSelected) return 'bg-indigo-50/80 border-indigo-200';
    if (isDisabled)
      return 'bg-zinc-50/50 border-transparent opacity-40 cursor-not-allowed';
    return 'bg-white border-zinc-100 hover:border-zinc-200 hover:bg-zinc-50/50';
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditModalOpen(true);
  };

  return (
    <>
      <button
        type="button"
        onClick={onSelect}
        disabled={isDisabled}
        className={cn(
          'w-full text-left rounded-lg px-2.5 py-2 transition-all border group',
          getButtonStyle(),
        )}
      >
        <div className="flex items-center gap-2.5">
          {/* Channel icon with background */}
          <div
            className={cn(
              'flex items-center justify-center w-6 h-6 rounded-md flex-shrink-0',
              isSelected ? 'bg-indigo-100' : 'bg-zinc-100',
            )}
          >
            <Icon
              className={cn(
                'h-3.5 w-3.5',
                isSelected ? 'text-indigo-600' : 'text-zinc-500',
              )}
            />
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0">
            {/* Primary value + secondary on same line for compactness */}
            <p
              className={cn(
                'text-[12px] font-medium truncate',
                isSelected ? 'text-indigo-900' : 'text-zinc-700',
              )}
            >
              {formatted.primary}
              {formatted.secondary && (
                <span className="text-zinc-400 font-normal">
                  {' · '}
                  {formatted.secondary}
                </span>
              )}
            </p>

            {/* Department label if present */}
            {contact.label && (
              <p className="text-[10px] text-zinc-400 truncate">
                {contact.label}
              </p>
            )}
          </div>

          {/* Edit button for insurance carriers */}
          {isInsuranceCarrier && !isDisabled && (
            <button
              type="button"
              onClick={handleEditClick}
              className={cn(
                'p-1 rounded opacity-0 group-hover:opacity-100 transition-opacity',
                'hover:bg-zinc-200/60 text-zinc-400 hover:text-zinc-600',
              )}
              title="Edit contact"
            >
              <Pencil className="h-3 w-3" />
            </button>
          )}

          {/* Selection indicator */}
          {isSelected && (
            <Check className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
          )}
        </div>
      </button>

      {/* Edit modal for insurance carrier contacts */}
      {isInsuranceCarrier && (
        <EditContactHandleModal
          open={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          contact={contact}
        />
      )}
    </>
  );
};
